import { addMinutes, differenceInYears, parseISO } from 'date-fns';
import { AgeGroups, Schedule } from '@/store/modules/job/types';
import { JobPostDraft, ScheduleTypes } from '../types';

const roundUpToNearest30Minutes = (date: Date): Date => {
  const remainder = date.getMinutes() % 30;
  return remainder === 0 ? date : addMinutes(date, 30 - remainder);
};

const getLocationData = (
  earliestCareSession
): { zipCode: string; location: { x: number; y: number }; locationDetails: string } => {
  return {
    zipCode: earliestCareSession.location.details.match(/\d{5}/)?.[0] || null,
    locationDetails: earliestCareSession.location.details.split(',').slice(1).join(',').trim() || null,
    location: {
      y: earliestCareSession.location.geoPoint.y,
      x: earliestCareSession.location.geoPoint.x,
    },
  };
};

const getScheduleType = (scCareSessions): ScheduleTypes => {
  const hasSingleCareSession: boolean = scCareSessions.length === 1;
  if (hasSingleCareSession) {
    const isCareSessionOvernight = new Date(scCareSessions[0].start).getDate() !== new Date(scCareSessions[0].end).getDate();
    return isCareSessionOvernight ? ScheduleTypes.OCCASIONAL : ScheduleTypes.ONE_TIME;
  }
  return ScheduleTypes.OCCASIONAL;
};

const getScheduleData = (earliestCareSession, scCareSessions): { scheduleType: ScheduleTypes; schedule: Schedule } => {
  const scheduleType = getScheduleType(scCareSessions);
  const startDate: string = new Date(earliestCareSession?.start).toISOString();
  const endTime: string = roundUpToNearest30Minutes(new Date(earliestCareSession?.end)).toISOString();
  const startTime: string = roundUpToNearest30Minutes(new Date(earliestCareSession?.start)).toISOString();
  const isStartTimeFlexible: boolean = false;
  const isStartDateFlexible: boolean = false;

  if (scheduleType === ScheduleTypes.OCCASIONAL) {
    return {
      scheduleType,
      schedule: {
        occasionalJobSchedule: {
          isStartDateFlexible,
          startDate,
        },
      },
    };
  }

  if (scheduleType === ScheduleTypes.ONE_TIME) {
    return {
      scheduleType,
      schedule: {
        oneTimeJobSchedule: {
          endTime,
          isStartTimeFlexible,
          startTime,
          startDate,
        },
      },
    };
  }

  throw new Error(`Unsupported schedule type: ${scheduleType}`);
};

const getNumberOfChildren = (activeCareRecipients): number => {
  // if child count is greater than 5, set it to 5
  return activeCareRecipients.length > 5 ? 5 : activeCareRecipients.length;
};

const getAgeGroups = (activeCareRecipients): AgeGroups[] => {
  const ageGroups = activeCareRecipients.map((child) => {
    const dateOfBirth = parseISO(child.dateOfBirth);
    const age = differenceInYears(new Date(), dateOfBirth); // Calculate age in years UTC

    if (age < 1) return AgeGroups.INFANT;
    if (age < 3) return AgeGroups.TODDLER;
    if (age < 6) return AgeGroups.PRE_SCHOOL;
    if (age < 11) return AgeGroups.ELEMENTARY;
    return AgeGroups.JUNIOR_HIGH;
  });

  // remove duplicates
  return Array.from(new Set(ageGroups));
};

export const extractJobDataFromCareSessions = (scCareSessions) => {
  if (!scCareSessions || scCareSessions.length === 0) {
    throw new Error('Invalid care sessions data provided');
  }

  try {
    const earliestCareSession = scCareSessions[0];
    const activeCareRecipients = earliestCareSession.individualCareRecipientDetails.filter(
      (careRecipient) => careRecipient.status !== 'Cancelled'
    );
    const { location, zipCode, locationDetails } = getLocationData(earliestCareSession);
    const { schedule, scheduleType } = getScheduleData(earliestCareSession, scCareSessions);
    const numberOfChildren = getNumberOfChildren(activeCareRecipients);
    const ageGroups = getAgeGroups(activeCareRecipients);

    const jobData: JobPostDraft = {
      scheduleType,
      ageGroups,
      location,
      locationDetails,
      zipCode,
      schedule,
      numberOfChildren,
    };

    return jobData;
  } catch (error) {
    // eslint-disable-next-line no-console
    throw new Error('Error mapping care session to job data');
  }
};

export default extractJobDataFromCareSessions;
